import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef, ViewChild, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { count, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { WorkflowDatasourceService } from '../../../../services/workflow-datasource/workflow-datasource.service';
import { HelpersService } from '../../../../services/helpers/helpers.service';
import { UtilsService } from '../../../../services/utils/utils.service';
import { WorkflowsService } from '../../../../services/workflows/workflows.service';
import { SettingsService } from '../../../../services/settings';
import { AccountsService } from '../../../../services/accounts/accounts.service';

declare const Helpers: any;

interface ActionOption {
  key?: string;
  value?: any;
  index?: number;
  options?: any[];
  valueToSet?: any;
  is_mandatory?: boolean;
  dom_element?: HTMLElement;
  show_if?: string;
  'show-if'?: string;
  required?: boolean;
  type?: string;
  name?: string;
  placeholder?: string;
  datasource?: any;
  values?: any[];
  multi_line?: boolean;
  isKeyValue?: boolean;
  fieldToUpdate?: string;
  dataType?: string;
  visible?: boolean;
  keyValuePairs?: Array<{ key: string; value: string; index: number }>;
  hierarchyValues?: any[];
  hierarchySelected?: string;
  hierarchyParentSelected?: string;
  /** Show manager email search UI (when {{managers_list}} selected) */
  showManagersSearch?: boolean;
  managerSearchText?: string;
  managerSearchResults?: Array<{ _id: string; email_address: string }>;
  managerSearching?: boolean;
  /** Template picker (type === 'component') */
  templateComponentType?: 'email' | 'sms' | 'whatsapp';
  templateOptions?: Array<{ id: string; name: string; text?: string; template_language?: string; raw?: any }>;
  templatePreviewText?: string;
  templateLoading?: boolean;
  is_multi?: boolean;
  loading?: boolean;
}

interface KeyValueConfig {
  key: string | null;
  value: string | null;
  index: number;
}

interface ActionConfig {
  key: string | null;
  value: string | null;
  index: number;
  options: any[];
}

interface ApiOptions {
  method: string;
  url: string;
  authentication_required: boolean;
  data: any;
  should_save_api_response: boolean;
  api_response_config: any;
  authorization: {
    auth_type: string;
    body: any;
  };
  auth_params: any;
  parameter_json: boolean;
  log_text?: string;
}

interface ApiAction {
  action_id: string;
  options: ApiOptions;
  retry: boolean;
  uuid?: string;
  should_execute_in_business_hours?: boolean;
}

interface AuthorizationTypeData {
  [key: string]: Array<{
    label: string;
    keyName: string;
    value: string;
    type: string;
  }>;
}

@Component({
  selector: 'dynamic-form-action-setup',
  imports: [CommonModule, FormsModule],
  standalone: true,
  templateUrl: './dynamic-form-action-setup.component.html',
  styleUrl: './dynamic-form-action-setup.component.scss'
})
export class DynamicFormActionSetupComponent implements OnInit, OnChanges, OnDestroy {
  @Input() readonly:boolean = true
  @Input() workflow: any = null;
  @Input() mode: string = 'create';
  @Output() actionAdded = new EventEmitter<any>();
  @Output() actionUpdated = new EventEmitter<any>();
  @Output() actionDeleted = new EventEmitter<number>();
  @Output() formReset = new EventEmitter<void>();

  // Action Management
  actionsList: any[] = [];
  actionsHash: { [key: string]: any } = {};
  availableActions: any[] = [];
  selectedActionId: string = '';
  selectedActionUuid: string = '';
  isAPIRequest: boolean = false;
  actionIndexToUpdate: number | null = null;
  private pendingEdit: { actionId: string; index: number } | null = null;

  // Form State
  optionsBasedOnActions: { [key: string]: ActionOption } = {};
  renderOptionCount: number = 0;
  activeTab: 'parameters' | 'authorization' | 'response' = 'parameters';

  // Parameters Tab
  parameterKeyPair: boolean = true;
  keyPairConfig: KeyValueConfig[] = [];
  paramsJsonFlag: boolean = false;

  // Authorization Tab
  authRequired: boolean = false;
  authorizationTypeName: string = 'oauth';
  authorizationTypeData: AuthorizationTypeData = {
    'oauth': [
      { label: 'Consumer Key', keyName: 'app_key', value: '', type: 'input' },
      { label: 'Consumer Secret', keyName: 'secret_key', value: '', type: 'input' },
      { label: 'Access Token', keyName: 'oauth_token', value: '', type: 'input' },
      { label: 'Token Secret', keyName: 'user_oauth_token_secret', value: '', type: 'input' }
    ],
    'bearerToken': [
      { label: 'Bearer Token', keyName: 'bearer_token', value: '', type: 'input' },
      { label: 'Token', keyName: 'bearer_token_body', value: '', type: 'multiLine' }
    ],
    'basicAuth': [
      { label: 'Username', keyName: 'username', value: '', type: 'input' },
      { label: 'Password', keyName: 'password', value: '', type: 'input' }
    ],
    'digestAuth': [
      { label: 'Username', keyName: 'username', value: '', type: 'input' },
      { label: 'Password', keyName: 'password', value: '', type: 'input' }
    ]
  };

  authorizationTypeNameMap: { [key: string]: string } = {
    'oauth': 'OAuth1',
    'bearerToken': 'Bearer Token',
    'basicAuth': 'Basic Auth',
    'digestAuth': 'Digest Auth'
  };

  // Response Tab
  isSaveApi: boolean = false;
  configs: ActionConfig[] = [];
  selectedOptions: { [key: string]: number } = {};
  apiResponseFlag: boolean = false;

  // API Request Options
  requestOptions: any = {
    requestType: 'POST',
    requestUrl: '',
    urlMarginLeft: '0px',
    parameters: { json: '{}' },
    authorisation: { json: '{}' },
    log_text: ''
  };

  // Business Hours
  performActionOnBusinessHour: boolean = false;
  isBusinessHourSetup: boolean = false;

  // Retry
  retry: boolean = false;

  // Special Action Fields
  principal_entity_id: string = '';
  content_template_id: string = '';
  template_language: string = '';
  template_text: string = '';

  // Rendered option keys (excludes uuid); stable order for template
  renderedOptionKeys: string[] = [];
  keepOrder = () => 0;

  // Custom dropdown state
  openDropdownId: string | null = null;

  requestTypeOptions = [
    { id: 'GET', name: 'GET' },
    { id: 'POST', name: 'POST' },
    { id: 'PUT', name: 'PUT' },
    { id: 'PATCH', name: 'PATCH' },
    { id: 'DELETE', name: 'DELETE' }
  ];

  authorizationTypeOptions = [
    { id: 'oauth', name: 'OAuth1' },
    { id: 'bearerToken', name: 'Bearer Token' },
    { id: 'basicAuth', name: 'Basic Auth' },
    { id: 'digestAuth', name: 'Digest Auth' }
  ];

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target?.closest?.('.custom-dropdown')) {
      this.openDropdownId = null;
    }
  }

  toggleDropdown(dropdownId: string, event?: Event): void {
    event?.stopPropagation();
    if(this.readonly){
      return
    }
    this.openDropdownId = this.openDropdownId === dropdownId ? null : dropdownId;
  }

  closeDropdown(): void {
    this.openDropdownId = null;
  }

  isDropdownOpen(dropdownId: string): boolean {
    return this.openDropdownId === dropdownId;
  }

  getDropdownLabel(options: any[], value: any, placeholder: string): string {
    if (value === undefined || value === null || value === '') {
      return placeholder;
    }
    const match = _.find(options, (opt: any) => String(opt.id) === String(value));
    return match?.name || match?.text || placeholder;
  }

  /** Flatten list values (incl. children/optgroups) for custom dropdown */
  getListDropdownOptions(option: ActionOption): any[] {
    const result: any[] = [];
    const valueKey = option?.datasource?.valuesToDisplay?.key || 'id';
    const textKey = option?.datasource?.valuesToDisplay?.value || 'text';

    _.each(option?.values || [], (item: any) => {
      if (item?.children?.length) {
        result.push({
          id: `__group__${item.text || item.name}`,
          name: item.text || item.name,
          isGroup: true
        });
        _.each(item.children, (child: any) => {
          result.push({
            id: child[valueKey] || child.id || child._id,
            name: child[textKey] || child.text || child.name,
            isGroup: false
          });
        });
      } else {
        result.push({
          id: item?.id ?? item?._id ?? item,
          name: item?.text ?? item?.name ?? item?.label ?? String(item),
          isGroup: false
        });
      }
    });
    return result;
  }

  getListDropdownDisplayValue(option: ActionOption): any {
    const valueToSet = option?.valueToSet;
    if (typeof valueToSet === 'string' && valueToSet.indexOf('{{hierarchy.') === 0) {
      return '{{hierarchy}}';
    }
    if (valueToSet === '{{hierarchy}}' || option?.hierarchyParentSelected === '{{hierarchy}}') {
      return '{{hierarchy}}';
    }
    if (
      valueToSet === '{{managers_list}}' ||
      option?.showManagersSearch ||
      (Array.isArray(valueToSet) && valueToSet.length > 0 && valueToSet[0]?._id)
    ) {
      return '{{managers_list}}';
    }
    return valueToSet || '';
  }

  getResponseFieldOptions(config: ActionConfig): any[] {
    return _.map(config?.options || [], (opt: any) => ({
      id: opt.field_id || opt.id || opt._id || opt.uuid,
      name: opt.name || opt.text || opt.display_name
    }));
  }

  getActionDropdownOptions(): any[] {
    return _.map(this.actionsList || [], (action: any) => ({
      id: action.id,
      name: action.text || action.name
    }));
  }

  onCustomSelect(dropdownId: string, value: any, handler: string, ...args: any[]): void {
    this.closeDropdown();
    switch (handler) {
      case 'action':
        this.onActionSelect(value || '');
        break;
      case 'requestType':
        this.requestOptions.requestType = value;
        break;
      case 'authorizationType':
        this.authorizationTypeName = value;
        this.onAuthorizationTypeChange(value);
        break;
      case 'responseField':
        if (args[0] !== undefined) {
          const config = this.configs[args[0]];
          if (config) {
            config.key = value;
          }
          this.addMoreOption(args[0], value);
        }
        break;
      case 'listOption':
        if (args[0] !== undefined) {
          this.onListOptionChange(args[0], value);
        }
        break;
      case 'hierarchy':
        if (args[0] !== undefined) {
          this.onHierarchyChildChange(args[0], value);
        }
        break;
      case 'template':
        if (args[0] !== undefined) {
          this.onTemplateSelect(args[0], value);
        }
        break;
    }
    this.cdr.detectChanges();
  }

  // Validation Flags
  authBearerToknFlag: boolean = false;
  isAuthData: boolean = false;
  authErrorMsg: string = '';

  // Template Config Keys
  emailNotificationActionTemplateConfig: string = '';
  whatsappNotificationActionTemplateConfig: string = '';
  smsNotificationActionTemplateConfig: string = '';

  @ViewChild('optionsFormContainer', { static: false }) optionsFormContainer!: ElementRef;

  private destroy$ = new Subject<void>();
  private managerSearch$ = new Subject<{ optionKey: string; text: string }>();
  readonly maxManagerSelections = 5;

  constructor(
    private cdr: ChangeDetectorRef,
    private workflowDatasource: WorkflowDatasourceService,
    private helpers: HelpersService,
    private utils: UtilsService,
    private workflowsService: WorkflowsService,
    private settingsService: SettingsService,
    private accountsService: AccountsService
  ) { }

  ngOnInit(): void {
    this.initializeComponent();
    this.managerSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => a.optionKey === b.optionKey && a.text === b.text),
        takeUntil(this.destroy$)
      )
      .subscribe(({ optionKey, text }) => this.searchManagers(optionKey, text));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workflow']) {
      this.initializeComponent();
    }
  }

  initializeComponent(): void {
    // Initialize default values
    if (this.keyPairConfig.length === 0) {
      this.keyPairConfig = [{ key: null, value: null, index: 0 }];
    }
    if (this.configs.length === 0) {
      this.configs = [{ key: null, value: null, index: 1, options: [] }];
    }

    this.actionsList = [];
    this.actionsHash = {};
    const brandDetails = this.helpers.getBrandDetails();
    this.isBusinessHourSetup = this.utils.getProp(brandDetails, 'conversation_config.actions.business_timings') &&
      !_.isEmpty(this.utils.getProp(brandDetails, 'conversation_config.actions.business_timings'));

    // Fetch workflow actions
    this.fetchWorkflowActions();
    this.populateAvailableActions();
  }

  fetchWorkflowActions(): void {
    // Fetch actions (type 2 = Actions)
    this.actionsList = [];
    this.actionsHash = {};
    this.workflowsService.getWorkflowDataByType(2).subscribe({
      next: (response: any) => {
        if (response?.data?.code === 0 && response?.data?.data) {
          this.actionsList = this.helpers.deepClone(response.data.data);
          this.resetActionHash();
        }
      },
      error: (error: any) => {
        console.error('Error fetching workflow actions:', error);
        this.showError('Failed to load actions. Please try again.');

      }
    });
  }

  resetActionHash(): void {
    this.actionsHash = {};
    this.actionsList = _.map(this.actionsList, (action: any) => {
      action.id = action._id;
      action.text = action.name;
      this.actionsHash[action._id] = action;
      return action;
    });

    // Apply edit that was queued before actions finished loading
    if (this.pendingEdit) {
      const { actionId, index } = this.pendingEdit;
      this.pendingEdit = null;
      this.selectedActionId = actionId;
      this.actionIndexToUpdate = index;
      this.selectedActionUuid = this.workflow?.actions?.[index]?.uuid || '';
      this.getFormBasedOnAction(actionId, index);
    }
  }

  populateAvailableActions(): void {
    if (this.workflow?.actions) {
      this.availableActions = _.map(this.workflow.actions, (action: any) => {
        action.name = action.name || action.action_id;
        return action;
      });
    } else {
      this.availableActions = [];
    }
  }

  onActionSelect(actionId: string): void {
    this.selectedActionId = actionId || '';
    if (!actionId) {
      this.optionsBasedOnActions = {};
      this.isAPIRequest = false;
      this.cdr.detectChanges();
      return;
    }
    // Selecting from dropdown for a new action clears edit index only when not editing
    this.getFormBasedOnAction(actionId, this.actionIndexToUpdate);
  }

  getFormBasedOnAction(actionId: string, index?: number | null): void {
    if (!actionId) {
      return;
    }

    const brandDetails = this.helpers.getBrandDetails();
    this.isBusinessHourSetup = !!(this.utils.getProp(brandDetails, 'conversation_config.actions.business_timings') &&
      !_.isEmpty(this.utils.getProp(brandDetails, 'conversation_config.actions.business_timings')));

    const schemaFromHash = this.utils.getProp(this.actionsHash, `${actionId}.schema.options`);
    if (!schemaFromHash) {
      // Actions not loaded yet or unknown action id
      if (_.isEmpty(this.actionsHash)) {
        this.pendingEdit = (index !== null && index !== undefined)
          ? { actionId, index }
          : this.pendingEdit;
      }
      return;
    }

    // Clone so we never mutate actionsHash schema
    const selectedActionSchemaOptions = this.helpers.deepClone(schemaFromHash);
    const isUpdateMode = index !== null && index !== undefined;

    if (isUpdateMode) {
      selectedActionSchemaOptions['uuid'] = {
        valueToSet: this.workflow?.actions?.[index]?.uuid || ''
      };
      this.selectedActionUuid = this.workflow?.actions?.[index]?.uuid || '';
      this.mapPrepopulateValue(selectedActionSchemaOptions, index as number, actionId);

      if (actionId === 'SMSNotify') {
        this.principal_entity_id = this.utils.getProp(this.workflow, `actions.${index}.options.principal_entity_id`) || '';
        this.content_template_id = this.utils.getProp(this.workflow, `actions.${index}.options.content_template_id`) || '';
      }
      if (actionId === 'WhatsAppNotify') {
        this.template_language = this.utils.getProp(this.workflow, `actions.${index}.options.template_language`) || '';
        this.template_text = this.utils.getProp(this.workflow, `actions.${index}.options.template_text`) || '';
      }
    } else {
      for (const key in selectedActionSchemaOptions) {
        if (selectedActionSchemaOptions[key]?.valueToSet !== undefined) {
          delete selectedActionSchemaOptions[key].valueToSet;
        }
      }
      this.selectedActionUuid = '';
    }

    this.optionsBasedOnActions = selectedActionSchemaOptions;
    this.selectedActionId = actionId;

    if (actionId === 'APIRequest') {
      this.isAPIRequest = true;
      this.getFieldDetails();
    } else {
      this.isAPIRequest = false;
      this.renderForm(this.optionsBasedOnActions, actionId);
    }

    this.cdr.detectChanges();
  }

  mapPrepopulateValue(schemaOption: any, index: number, actionId: string): void {
    const selectedAction = this.workflow?.actions?.[index];
    if (!selectedAction) {
      return;
    }

    this.parameterKeyPair = true;
    this.authRequired = false;
    this.retry = !!selectedAction.retry;

    if (actionId === 'APIRequest') {
      this.prepareUpdateData(selectedAction);
    } else if (selectedAction.action_id) {
      for (const key in (selectedAction.options || {})) {
        if (!schemaOption[key]) {
          continue;
        }
        const optionValue = this.utils.getProp(selectedAction, `options.${key}`);
        // Object-type fields are stored flat but UI expects { data: {...} }
        if (schemaOption[key].type === 'object' && optionValue && typeof optionValue === 'object' && !optionValue.data) {
          schemaOption[key].valueToSet = { data: optionValue };
        } else {
          schemaOption[key].valueToSet = optionValue;
        }
      }
      if (schemaOption['uuid']) {
        schemaOption['uuid'].valueToSet = this.utils.getProp(selectedAction, 'uuid') || '';
      }

      if (selectedAction.should_execute_in_business_hours) {
        this.changeIsBusinessHour(true);
      } else {
        this.changeIsBusinessHour(false);
      }
    }
  }

  /**
   * Matching old beta-action renderForm:
   * - evaluate show-if / is_mandatory
   * - load list values (static or datasource)
   * - init object key-value rows
   * - prepare hierarchy child state
   */
  async renderForm(options: { [key: string]: ActionOption }, selectedAction: string): Promise<void> {
    if (!options || _.isEmpty(options)) {
      this.renderedOptionKeys = [];
      this.cdr.detectChanges();
      return;
    }

    this.evaluateShowIfConditions(options, selectedAction);

    this.renderedOptionKeys = _.keys(options).filter((key) => key !== 'uuid');

    const loadPromises: Promise<void>[] = [];

    for (const key of this.renderedOptionKeys) {
      const option = options[key];
      if (!option) {
        continue;
      }

      // Default visibility
      if (option.visible === undefined) {
        const showIf = option['show-if'] || option.show_if;
        option.visible = !showIf || option.is_mandatory !== false || !!option.valueToSet;
      }

      if (option.type === 'list') {
        option.loading = true;
        loadPromises.push(
          this.fetchOptionDataAndPopulate(option).then(async (values) => {
            option.values = values || [];
            option.is_multi = !!_.get(option, 'datasource.is_multi');
            option.loading = false;

            // If prepopulated hierarchy expression, load child hierarchy levels
            const valueToSet = option.valueToSet;
            if (typeof valueToSet === 'string' && valueToSet.indexOf('{{hierarchy.') > -1) {
              option.hierarchySelected = valueToSet;
              option.hierarchyParentSelected = '{{hierarchy}}';
              await this.loadHierarchyChildOptions(key, '{{hierarchy}}', option);
              return;
            }
            if (valueToSet === '{{hierarchy}}') {
              option.hierarchyParentSelected = '{{hierarchy}}';
              await this.loadHierarchyChildOptions(key, '{{hierarchy}}', option);
              return;
            }
            // Matching old edit preload for managers_list selections
            if (Array.isArray(valueToSet) && valueToSet.length && valueToSet[0]?._id) {
              option.showManagersSearch = true;
              option.managerSearchText = '';
              option.managerSearchResults = [];
            }
          })
        );
      } else if (option.type === 'object') {
        this.initObjectKeyValuePairs(option);
      } else if (option.type === 'component') {
        // Matching old getComponent — email / SMS / WhatsApp template pickers
        option.visible = option.visible !== false;
        this.setupTemplateComponent(key, option);
      }
    }

    await Promise.all(loadPromises);
    this.optionsBasedOnActions = { ...options };
    this.cdr.detectChanges();
  }

  /**
   * Matching old renderForm show-if pass — sets is_mandatory / visible
   */
  private evaluateShowIfConditions(options: { [key: string]: ActionOption }, selectedAction?: string): void {
    const optionsKeyMapper: { [key: string]: any } = {};
    _.each(_.keys(options), (k) => {
      optionsKeyMapper['options.' + k] = options[k]?.valueToSet;
    });

    _.each(options, (dataValue, dataKey) => {
      const conditions = dataValue?.['show-if'] || dataValue?.show_if;
      if (!conditions) {
        dataValue.visible = true;
        if (dataValue.is_mandatory === undefined) {
          dataValue.is_mandatory = !!dataValue.required;
        }
        return;
      }

      if (!this.isValidateShowConditions(conditions, optionsKeyMapper, selectedAction || this.selectedActionId)) {
        dataValue.is_mandatory = false;
        dataValue.visible = false;
        return;
      }

      let evalCondition = conditions;
      let evaluated = false;
      _.each(optionsKeyMapper, (optionReplaceVal, optionsKey) => {
        if (evalCondition.indexOf(optionsKey) > -1 && optionReplaceVal !== undefined && optionReplaceVal !== null && optionReplaceVal !== '') {
          evalCondition = evalCondition.replace(new RegExp(optionsKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), JSON.stringify(optionReplaceVal));
          evaluated = true;
        }
      });

      if (!evaluated) {
        // show-if present but parent value not set yet → hide
        dataValue.is_mandatory = false;
        dataValue.visible = false;
        return;
      }

      const result = this.safeEvalCondition(evalCondition);
      dataValue.is_mandatory = !!result;
      dataValue.visible = !!result;
    });
  }

  /** Matching old isValidateShowConditions (AutoUpdate special-case) */
  private isValidateShowConditions(conditions: string, optionsKeyMapper: any, actionId?: string): boolean {
    if (actionId === 'AutoUpdate' && conditions) {
      const expressionDetail = this.fetchOperatorAndOperands(conditions);
      if (!expressionDetail.operator) {
        return true;
      }
      const evalLHS = _.get(optionsKeyMapper, expressionDetail.lhs);
      return this.safeEvalCondition(
        JSON.stringify(evalLHS) + expressionDetail.operator + expressionDetail.rhs
      );
    }
    return true;
  }

  private fetchOperatorAndOperands(filter: string): { lhs: string; rhs: string; operator: string | null } {
    let operator: string | null = null;
    if (filter.indexOf('==') >= 0) {
      operator = '==';
    } else if (filter.indexOf('!=') >= 0 || filter.indexOf('<>') >= 0) {
      operator = '!=';
    } else if (filter.indexOf('>=') >= 0) {
      operator = '>=';
    } else if (filter.indexOf('<=') >= 0) {
      operator = '<=';
    } else if (filter.indexOf('>') >= 0) {
      operator = '>';
    } else if (filter.indexOf('<') >= 0) {
      operator = '<';
    } else if (filter.indexOf('$has') >= 0) {
      operator = '$has';
    }
    if (!operator) {
      return { lhs: filter, rhs: '', operator: null };
    }
    const operands = filter.split(operator);
    return { lhs: (operands[0] || '').trim(), rhs: (operands[1] || '').trim(), operator };
  }

  private safeEvalCondition(expression: string): boolean {
    try {
      // Support simple comparisons only (no arbitrary code)
      const match = expression.match(/^(.*?)\s*(==|!=|>=|<=|>|<)\s*(.*)$/);
      if (!match) {
        return true;
      }
      let lhs: any = match[1].trim();
      let rhs: any = match[3].trim();
      try { lhs = JSON.parse(lhs); } catch { /* keep string */ }
      try { rhs = JSON.parse(rhs); } catch {
        rhs = rhs.replace(/^['"]|['"]$/g, '');
      }
      switch (match[2]) {
        case '==': return lhs == rhs;
        case '!=': return lhs != rhs;
        case '>': return lhs > rhs;
        case '<': return lhs < rhs;
        case '>=': return lhs >= rhs;
        case '<=': return lhs <= rhs;
        default: return true;
      }
    } catch {
      return true;
    }
  }

  /**
   * Matching old fetchOptionDataAndPopulate
   */
  private fetchOptionDataAndPopulate(optionDetail: ActionOption): Promise<any[]> {
    return new Promise((resolve) => {
      if (optionDetail?.values && optionDetail.values.length) {
        if (_.isObject(optionDetail.values[0]) && !_.isArray(optionDetail.values[0])) {
          resolve(optionDetail.values);
        } else {
          resolve(_.map(optionDetail.values, (value) => ({ id: value, text: value })));
        }
        return;
      }

      if (optionDetail?.datasource) {
        this.getDataFromDataSource(optionDetail).then((value) => {
          resolve(value || []);
        });
        return;
      }

      resolve([]);
    });
  }

  /**
   * Matching old getDataFromDataSource via WorkflowDatasourceService
   */
  private getDataFromDataSource(optionDetail: ActionOption): Promise<any[]> {
    return new Promise((resolve) => {
      if (!optionDetail?.datasource) {
        resolve([]);
        return;
      }

      this.workflowDatasource.getData(optionDetail.datasource, (error: any, data: any) => {
        if (error) {
          resolve([]);
          return;
        }

        if (_.get(optionDetail, 'datasource.dataFromExpression')) {
          resolve((_.get(data, 'data')) ? data.data : (data || []));
          return;
        }

        const valuesToDisplay = _.get(optionDetail, 'datasource.valuesToDisplay');
        if (valuesToDisplay && data?.data) {
          const options = _.map(data.data, (option: any) => {
            const response: any = {
              id: option[valuesToDisplay.key],
              text: option[valuesToDisplay.value]
            };
            if (_.get(optionDetail, 'datasource.valuesToDisplay.selected_fields.length')) {
              _.each(optionDetail.datasource.valuesToDisplay.selected_fields, (field: string) => {
                response[field] = option[field];
              });
            }
            // Preserve children / possible_values for hierarchy
            if (option.children) {
              response.children = option.children;
            }
            if (option.possible_values) {
              response.possible_values = option.possible_values;
            }
            return response;
          });
          resolve(options);
        } else {
          resolve([]);
        }
      });
    });
  }

  /** Matching old prepareHierarchyLevelDetail */
  private prepareHierarchyLevelDetail(data: any): any[] {
    const detail: any[] = [];
    if (!data || typeof data !== 'object') {
      return detail;
    }
    const keys = Object.keys(data);
    const lastKey = keys[keys.length - 1];
    const lastVal = data[lastKey];
    if (lastVal && lastVal.length) {
      _.each(lastVal, (val: string) => {
        const normalized = String(val).replace(/ /g, '_');
        detail.push({
          id: `{{hierarchy.${normalized}.manager}}`,
          text: val
        });
      });
    }
    return detail;
  }

  private async loadHierarchyChildOptions(
    optionKey: string,
    selectedOption: string,
    optionRef?: ActionOption
  ): Promise<void> {
    const attributeForKey = optionRef || this.optionsBasedOnActions[optionKey];
    if (!attributeForKey) {
      return;
    }
    const availableOptions = attributeForKey.values || [];
    const selectedOptionDetail: any = _.find(
      availableOptions,
      (opt: any) => String(opt?.id) === String(selectedOption)
    );

    if (selectedOption === '{{hierarchy}}' && !_.isEmpty(selectedOptionDetail) && selectedOptionDetail.possible_values) {
      const hierarchyOptionDetail: any = {
        datasource: {
          expression: selectedOptionDetail.possible_values,
          type: 3,
          dataFromExpression: true
        }
      };
      const data = await this.getDataFromDataSource(hierarchyOptionDetail);
      attributeForKey.hierarchyValues = this.prepareHierarchyLevelDetail(
        Array.isArray(data) ? this.arrayToHierarchyMap(data) : data
      );
      if ((!attributeForKey.hierarchyValues || !attributeForKey.hierarchyValues.length) && Array.isArray(data)) {
        attributeForKey.hierarchyValues = data;
      }
    } else {
      attributeForKey.hierarchyValues = [];
    }

    if (this.optionsBasedOnActions[optionKey]) {
      this.optionsBasedOnActions[optionKey] = attributeForKey;
      this.optionsBasedOnActions = { ...this.optionsBasedOnActions };
    }
  }

  private arrayToHierarchyMap(data: any): any {
    // When expression returns array of level names directly
    if (Array.isArray(data) && data.length && typeof data[0] === 'string') {
      return { level: data };
    }
    return data;
  }

  private initObjectKeyValuePairs(option: ActionOption): void {
    const pairs: Array<{ key: string; value: string; index: number }> = [];
    const valueToSet = option.valueToSet;

    if (valueToSet?.data && typeof valueToSet.data === 'object') {
      let idx = 0;
      _.each(valueToSet.data, (val: any, key: string) => {
        pairs.push({ key, value: String(val ?? ''), index: idx++ });
      });
    } else if (valueToSet && typeof valueToSet === 'object' && !Array.isArray(valueToSet) && !valueToSet.data) {
      // Flat object stored from API
      let idx = 0;
      _.each(valueToSet, (val: any, key: string) => {
        if (key === 'data') {
          return;
        }
        // Skip numeric index bags from old format
        if (!isNaN(Number(key)) && val && typeof val === 'object' && ('key' in val || 'value' in val)) {
          pairs.push({ key: val.key || '', value: val.value || '', index: idx++ });
        } else if (isNaN(Number(key))) {
          pairs.push({ key, value: String(val ?? ''), index: idx++ });
        }
      });
    }

    if (!pairs.length) {
      pairs.push({ key: '', value: '', index: 0 });
    }
    option.keyValuePairs = pairs;
    this.syncObjectValueToSet(option);
  }

  private syncObjectValueToSet(option: ActionOption): void {
    if (!option.keyValuePairs) {
      return;
    }
    const data: any = {};
    const indexed: any = {};
    option.keyValuePairs.forEach((pair, index) => {
      indexed[index + 1] = { key: pair.key, value: pair.value };
      if (pair.key) {
        data[pair.key] = pair.value;
      }
    });
    option.valueToSet = { ...indexed, data };
  }

  addObjectKeyValue(optionKey: string): void {
    const option = this.optionsBasedOnActions[optionKey];
    if (!option) {
      return;
    }
    if (!option.keyValuePairs) {
      option.keyValuePairs = [];
    }
    option.keyValuePairs.push({
      key: '',
      value: '',
      index: option.keyValuePairs.length
    });
    this.syncObjectValueToSet(option);
    this.cdr.detectChanges();
  }

  deleteObjectKeyValue(optionKey: string, pairIndex: number): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    const option = this.optionsBasedOnActions[optionKey];
    if (!option?.keyValuePairs || option.keyValuePairs.length <= 1) {
      return;
    }
    option.keyValuePairs.splice(pairIndex, 1);
    option.keyValuePairs.forEach((p, i) => p.index = i);
    this.syncObjectValueToSet(option);
    this.cdr.detectChanges();
  }

  onObjectKeyValueChange(optionKey: string, pairIndex: number, field: 'key' | 'value', value: string): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    const option = this.optionsBasedOnActions[optionKey];
    if (!option?.keyValuePairs?.[pairIndex]) {
      return;
    }
    option.keyValuePairs[pairIndex][field] = value;
    this.syncObjectValueToSet(option);
    this.evaluateShowIfConditions(this.optionsBasedOnActions, this.selectedActionId);
    this.cdr.detectChanges();
  }

  async onListOptionChange(optionKey: string, selectedValue: any): Promise<void> {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    const option = this.optionsBasedOnActions[optionKey];
    if (!option) {
      return;
    }

    if (selectedValue === '{{hierarchy}}') {
      // Matching old getPossibleValue: load child hierarchy levels
      option.showManagersSearch = false;
      option.managerSearchResults = [];
      option.managerSearchText = '';
      option.hierarchyParentSelected = '{{hierarchy}}';
      option.hierarchySelected = '';
      option.valueToSet = '';
      await this.loadHierarchyChildOptions(optionKey, '{{hierarchy}}', option);
    } else if (selectedValue === '{{managers_list}}') {
      // Matching old getPossibleValue managers_list: show AJAX manager search
      option.hierarchyValues = [];
      option.hierarchySelected = '';
      option.hierarchyParentSelected = '';
      option.showManagersSearch = true;
      option.managerSearchText = '';
      option.managerSearchResults = [];
      // Keep existing manager selections when re-selecting; otherwise start empty
      if (!Array.isArray(option.valueToSet) || !option.valueToSet[0]?._id) {
        option.valueToSet = [];
      }
    } else {
      option.showManagersSearch = false;
      option.managerSearchResults = [];
      option.managerSearchText = '';
      option.hierarchyParentSelected = '';
      option.valueToSet = selectedValue;
      option.hierarchyValues = [];
      option.hierarchySelected = '';
    }

    this.optionsBasedOnActions = { ...this.optionsBasedOnActions };
    this.evaluateShowIfConditions(this.optionsBasedOnActions, this.selectedActionId);
    this.cdr.detectChanges();
  }

  onHierarchyChildChange(optionKey: string, selectedValue: string): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    const option = this.optionsBasedOnActions[optionKey];
    if (!option) {
      return;
    }
    option.hierarchySelected = selectedValue;
    option.hierarchyParentSelected = selectedValue ? '{{hierarchy}}' : '';
    option.valueToSet = selectedValue;
    this.optionsBasedOnActions = { ...this.optionsBasedOnActions };
    this.evaluateShowIfConditions(this.optionsBasedOnActions, this.selectedActionId);
    this.cdr.detectChanges();
  }

  /** Matching old select2 managers_search_es — trigger debounced search */
  onManagerSearchInput(optionKey: string, text: string): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    const option = this.optionsBasedOnActions[optionKey];
    if (!option) {
      return;
    }
    option.managerSearchText = text;
    this.managerSearch$.next({ optionKey, text: (text || '').trim() });
  }

  private searchManagers(optionKey: string, text: string): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    const option = this.optionsBasedOnActions[optionKey];
    if (!option?.showManagersSearch) {
      return;
    }

    if (!text || text.length < 1) {
      option.managerSearchResults = [];
      option.managerSearching = false;
      this.cdr.detectChanges();
      return;
    }

    const brandDetails = this.helpers.getBrandDetails();
    const brandId = brandDetails?._id;
    if (!brandId) {
      option.managerSearchResults = [];
      option.managerSearching = false;
      this.cdr.detectChanges();
      return;
    }

    option.managerSearching = true;
    this.cdr.detectChanges();

    // Matching old ltms-workflow-edit-beta-action-config select2 ajax params
    const apiParams = {
      brand_id: brandId,
      text,
      search_type: 2, // search with email address
      page_number: 1,
      size: 10
    };

    this.settingsService.searchManager(apiParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const managers = _.get(response, 'data.managers') || _.get(response, 'managers') || [];
          const selectedIds = _.map(this.getSelectedManagers(option), (m: any) => m._id);
          option.managerSearchResults = _.map(
            _.filter(managers, (m: any) => m?._id && selectedIds.indexOf(m._id) < 0),
            (manager: any) => ({
              _id: manager._id,
              email_address: manager.email_address || manager.text || ''
            })
          );
          option.managerSearching = false;
          this.cdr.detectChanges();
        },
        error: () => {
          option.managerSearchResults = [];
          option.managerSearching = false;
          this.cdr.detectChanges();
        }
      });
  }

  getSelectedManagers(option: ActionOption): Array<{ _id: string; email_address: string }> {
    if (Array.isArray(option?.valueToSet) && option.valueToSet.length && option.valueToSet[0]?._id) {
      return option.valueToSet;
    }
    return [];
  }

  addManagerSelection(optionKey: string, manager: { _id: string; email_address: string }): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    const option = this.optionsBasedOnActions[optionKey];
    if (!option || !manager?._id) {
      return;
    }
    const selected = this.getSelectedManagers(option);
    if (selected.length >= this.maxManagerSelections) {
      return;
    }
    if (_.find(selected, { _id: manager._id })) {
      return;
    }
    selected.push({ _id: manager._id, email_address: manager.email_address });
    option.valueToSet = selected;
    option.managerSearchResults = _.filter(
      option.managerSearchResults || [],
      (m: any) => m._id !== manager._id
    );
    this.optionsBasedOnActions = { ...this.optionsBasedOnActions };
    this.evaluateShowIfConditions(this.optionsBasedOnActions, this.selectedActionId);
    this.cdr.detectChanges();
  }

  removeManagerSelection(optionKey: string, managerId: string): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    const option = this.optionsBasedOnActions[optionKey];
    if (!option) {
      return;
    }
    option.valueToSet = _.filter(this.getSelectedManagers(option), (m: any) => m._id !== managerId);
    this.optionsBasedOnActions = { ...this.optionsBasedOnActions };
    this.evaluateShowIfConditions(this.optionsBasedOnActions, this.selectedActionId);
    this.cdr.detectChanges();
  }

  isManagerSelected(option: ActionOption, managerId: string): boolean {
    return !!_.find(this.getSelectedManagers(option), { _id: managerId });
  }

  /**
   * Matching old getComponent — wire email / SMS / WhatsApp template pickers
   */
  private setupTemplateComponent(optionKey: string, option: ActionOption): void {
    const actionId = this.selectedActionId;

    if (actionId === 'SMSNotify') {
      option.templateComponentType = 'sms';
      this.smsNotificationActionTemplateConfig = optionKey;
      this.loadSmsTemplates(optionKey, option);
    } else if (actionId === 'WhatsAppNotify') {
      option.templateComponentType = 'whatsapp';
      this.whatsappNotificationActionTemplateConfig = optionKey;
      this.loadWhatsAppTemplates(optionKey, option);
    } else {
      // EmailNotify, TSHook, and other component-based actions use email templates
      option.templateComponentType = 'email';
      this.emailNotificationActionTemplateConfig = optionKey;
      this.loadEmailTemplates(optionKey, option);
    }
  }

  private getBrandContext(): { brandId: string; accountId: string; managerId: string } {
    const brandDetails = this.helpers.getBrandDetails() || {};
    const managerId =
      _.get(Helpers, 'managerDetails.manager._id') ||
      _.get(Helpers, 'managerDetails.loggedInManager._id') ||
      '';
    return {
      brandId: brandDetails._id || '',
      accountId: brandDetails.account_id || '',
      managerId
    };
  }

  private loadEmailTemplates(optionKey: string, option: ActionOption): void {
    const { brandId } = this.getBrandContext();
    if (!brandId) {
      option.templateOptions = [];
      return;
    }
    option.templateLoading = true;
    this.settingsService.getEmailTemplatesByBrandId(brandId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const list = _.get(response, 'data') || (Array.isArray(response) ? response : []);
          option.templateOptions = _.map(list, (t: any) => ({
            id: t._id || t.id,
            name: t.filename || t.name || t._id,
            raw: t
          }));
          option.templateLoading = false;
          this.optionsBasedOnActions = { ...this.optionsBasedOnActions };
          this.cdr.detectChanges();
        },
        error: () => {
          option.templateOptions = [];
          option.templateLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private loadSmsTemplates(optionKey: string, option: ActionOption): void {
    const { accountId } = this.getBrandContext();
    if (!accountId) {
      option.templateOptions = [];
      return;
    }
    option.templateLoading = true;
    this.accountsService.getAccountDetail(accountId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const account = _.get(response, 'account') || _.get(response, 'data.account') || {};
          const isDltEnabled = !!this.utils.getProp(account, 'conversation_config.feedbacks.sms.is_dlt_enabled');
          const principalEntityId = this.utils.getProp(account, 'conversation_config.feedbacks.sms.principal_entity_id') || '';
          const smsTemplates = this.utils.getProp(account, 'conversation_config.feedbacks.sms.sms_templates') || [];

          if (principalEntityId) {
            this.principal_entity_id = principalEntityId;
          }

          if (isDltEnabled && Array.isArray(smsTemplates) && smsTemplates.length) {
            option.templateOptions = _.map(
              _.filter(smsTemplates, (t: any) => t?.is_active !== false),
              (t: any) => ({
                id: t.id,
                name: (t.name || '') + (t.id || ''),
                text: t.text || '',
                raw: t
              })
            );
          } else {
            option.templateOptions = [];
          }

          // Prefill preview when editing
          if (option.valueToSet) {
            const selected = _.find(option.templateOptions, { id: option.valueToSet });
            option.templatePreviewText = selected?.text || this.template_text || '';
            this.content_template_id = option.valueToSet;
          }

          option.templateLoading = false;
          this.optionsBasedOnActions = { ...this.optionsBasedOnActions };
          this.cdr.detectChanges();
        },
        error: () => {
          option.templateOptions = [];
          option.templateLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private loadWhatsAppTemplates(optionKey: string, option: ActionOption): void {
    const { accountId } = this.getBrandContext();
    if (!accountId) {
      option.templateOptions = [];
      return;
    }
    option.templateLoading = true;
    this.accountsService.getAccountDetail(accountId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const account = _.get(response, 'account') || _.get(response, 'data.account') || {};
          const whatsappTemplates = this.utils.getProp(account, 'conversation_config.feedbacks.whatsapp.whatsapp_templates') || [];

          option.templateOptions = _.map(whatsappTemplates || [], (t: any) => ({
            id: t.template_id,
            name: `${t.template_name || ''}(${t.template_id || ''})`,
            text: t.template_text || '',
            template_language: t.template_language || '',
            raw: t
          }));

          if (option.valueToSet) {
            const selected = _.find(option.templateOptions, { id: option.valueToSet });
            option.templatePreviewText = selected?.text || this.template_text || '';
            if (selected?.template_language) {
              this.template_language = selected.template_language;
            }
          }

          option.templateLoading = false;
          this.optionsBasedOnActions = { ...this.optionsBasedOnActions };
          this.cdr.detectChanges();
        },
        error: () => {
          option.templateOptions = [];
          option.templateLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  getTemplateDropdownOptions(option: ActionOption): any[] {
    return _.map(option?.templateOptions || [], (t: any) => ({
      id: t.id,
      name: t.name
    }));
  }

  onTemplateSelect(optionKey: string, templateId: any): void {
    const option = this.optionsBasedOnActions[optionKey];
    if (!option) {
      return;
    }

    option.valueToSet = templateId || '';
    const selected = _.find(option.templateOptions || [], (t: any) => String(t.id) === String(templateId));

    if (option.templateComponentType === 'email') {
      // Matching send-template-id-to-workflow-action-component
      this.emailNotificationActionTemplateConfig = optionKey;
    } else if (option.templateComponentType === 'sms') {
      // Matching send-sms-template-id-to-workflow-action-component
      option.templatePreviewText = selected?.text || '';
      this.content_template_id = templateId || '';
      this.smsNotificationActionTemplateConfig = optionKey;
      if (this.optionsBasedOnActions['template']) {
        this.optionsBasedOnActions['template'].valueToSet = selected?.text || '';
      }
    } else if (option.templateComponentType === 'whatsapp') {
      // Matching send-whatsapp-template-id-to-workflow-action-component
      option.templatePreviewText = selected?.text || '';
      this.template_text = selected?.text || '';
      this.template_language = selected?.template_language || '';
      this.whatsappNotificationActionTemplateConfig = optionKey;
      if (!this.optionsBasedOnActions['template_text']) {
        this.optionsBasedOnActions['template_text'] = { type: 'string', valueToSet: this.template_text, visible: false };
      } else {
        this.optionsBasedOnActions['template_text'].valueToSet = this.template_text;
      }
      if (!this.optionsBasedOnActions['template_language']) {
        this.optionsBasedOnActions['template_language'] = { type: 'string', valueToSet: this.template_language, visible: false };
      } else {
        this.optionsBasedOnActions['template_language'].valueToSet = this.template_language;
      }
    }

    this.optionsBasedOnActions = { ...this.optionsBasedOnActions };
    this.evaluateShowIfConditions(this.optionsBasedOnActions, this.selectedActionId);
    this.cdr.detectChanges();
  }

  onTemplateFilePick(optionKey: string, event: Event): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }
    if (file.type !== 'text/html') {
      if (Helpers && Helpers.smallAlertBox) {
        Helpers.smallAlertBox('Error', 'Only HTML files can be uploaded', 'error');
      }
      input.value = '';
      return;
    }
    this.uploadEmailTemplateFile(optionKey, file);
    input.value = '';
  }

  private uploadEmailTemplateFile(optionKey: string, file: File): void {
    const option = this.optionsBasedOnActions[optionKey];
    if (!option) {
      return;
    }
    const { brandId, managerId } = this.getBrandContext();
    if (!brandId || !managerId) {
      if (Helpers && Helpers.smallAlertBox) {
        Helpers.smallAlertBox('Error', 'Brand or manager context missing', 'error');
      }
      return;
    }

    option.templateLoading = true;
    this.cdr.detectChanges();

    this.settingsService.uploadEmailTemplate(brandId, managerId, file)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const uploaded = _.get(response, 'data.response') || _.get(response, 'response') || _.get(response, 'data');
          if (response?.code === 0 || _.get(response, 'data.code') === 0) {
            const newId = uploaded?._id;
            const filename = uploaded?.filename || file.name;
            if (newId) {
              option.templateOptions = [
                ...(option.templateOptions || []),
                { id: newId, name: filename, raw: uploaded }
              ];
              this.onTemplateSelect(optionKey, newId);
            }
          } else if (Helpers && Helpers.smallAlertBox) {
            Helpers.smallAlertBox('Error', 'Error in uploading template', 'error');
          }
          option.templateLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          option.templateLoading = false;
          if (Helpers && Helpers.smallAlertBox) {
            Helpers.smallAlertBox('Error', 'Error in uploading template', 'error');
          }
          this.cdr.detectChanges();
        }
      });
  }

  previewEmailTemplate(optionKey: string): void {
    const option = this.optionsBasedOnActions[optionKey];
    const templateId = option?.valueToSet;
    if (!templateId) {
      if (Helpers && Helpers.smallAlertBox) {
        Helpers.smallAlertBox('Error', 'No preview available', 'error');
      }
      return;
    }
    this.settingsService.getEmailTemplatePreview(templateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const content = response?.content || _.get(response, 'data.content');
          if ((response?.code === 0 || content) && content) {
            const previewWindow = window.open();
            if (previewWindow) {
              previewWindow.document.write(content);
              previewWindow.document.title = 'Email Template';
            }
          } else if (Helpers && Helpers.smallAlertBox) {
            Helpers.smallAlertBox('Error', 'No preview available', 'error');
          }
        },
        error: () => {
          if (Helpers && Helpers.smallAlertBox) {
            Helpers.smallAlertBox('Error', 'No preview available', 'error');
          }
        }
      });
  }

  downloadEmailTemplate(optionKey: string): void {
    const option = this.optionsBasedOnActions[optionKey];
    const templateId = option?.valueToSet;
    if (!templateId) {
      if (Helpers && Helpers.smallAlertBox) {
        Helpers.smallAlertBox('Error', 'No preview available', 'error');
      }
      return;
    }
    const { brandId } = this.getBrandContext();
    this.settingsService.getEmailTemplatePreview(templateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          let html = response?.content || _.get(response, 'data.content');
          if ((response?.code === 0 || html) && html) {
            html = String(html)
              .replace(/#/g, '%23')
              .replace(/"/g, '%22')
              .replace(/'/g, '%27');
            const link = document.createElement('a');
            link.download = `${brandId || 'template'}.html`;
            link.href = 'data:text/html;charset=UTF-8,' + html;
            link.click();
          } else if (Helpers && Helpers.smallAlertBox) {
            Helpers.smallAlertBox('Error', 'No preview available', 'error');
          }
        },
        error: () => {
          if (Helpers && Helpers.smallAlertBox) {
            Helpers.smallAlertBox('Error', 'No preview available', 'error');
          }
        }
      });
  }

  onOptionValueChange(key: string, value: any): void {
    if (this.optionsBasedOnActions[key]) {
      this.optionsBasedOnActions[key].valueToSet = value;
      this.evaluateShowIfConditions(this.optionsBasedOnActions, this.selectedActionId);
      this.cdr.detectChanges();
    }
  }

  // Helper method to check if field should be shown
  shouldShowField(option: ActionOption): boolean {
    if (!option) {
      return false;
    }
    if (option.visible === false) {
      return false;
    }
    return true;
  }

  // Parameters Tab Methods
  toggleParameterView(view: 'keyPair' | 'json'): void {
    this.parameterKeyPair = view === 'keyPair';
    if (!this.parameterKeyPair && !_.isEmpty(this.keyPairConfig)) {
      let json = {};
      _.each(this.keyPairConfig, (config) => {
        if (config.key && config.key.trim() !== '') {
          json[config.key] = config.value || null;
        }
      });
      this.requestOptions.parameters.json = JSON.stringify(json, null, 2);
    } else if (this.parameterKeyPair && this.requestOptions.parameters.json && this.requestOptions.parameters.json.trim() !== '') {
      try {
        let json = JSON.parse(this.requestOptions.parameters.json);
        let count = 0;
        if (json && !_.isEmpty(json)) {
          let newKeyPairConfig: KeyValueConfig[] = [];
          _.each(json, (value, key) => {
            newKeyPairConfig.push({
              key: key,
              value: value || null,
              index: count
            });
            count++;
          });
          this.keyPairConfig = _.cloneDeep(newKeyPairConfig);
        } else {
          this.keyPairConfig = [{
            key: null,
            value: null,
            index: 0
          }];
        }

      } catch (error) {
        this.keyPairConfig = [{
          key: null,
          value: null,
          index: 0
        }]
      } finally {
        this.cdr.detectChanges();
      }
    }
  }

  addKeyPairInput(index: number, field_key?: string, field_value?: string): void {
    if (index === this.keyPairConfig.length - 1) {
      const newConfig: KeyValueConfig = {
        key: field_key || null,
        value: field_value || null,
        index: this.keyPairConfig.length
      };
      this.keyPairConfig.push(newConfig);
    } else {
      if (this.keyPairConfig[index]) {
        this.keyPairConfig[index].key = field_key || null;
        this.keyPairConfig[index].value = field_value || null;
      }
    }
  }

  deleteKeyPair(index: number): void {
    if (this.keyPairConfig.length > 1) {
      this.keyPairConfig.splice(index, 1);
      // Reindex
      this.keyPairConfig.forEach((config, idx) => {
        config.index = idx;
      });
    }
  }

  onKeyPairChange(index: number, field: 'key' | 'value', value: string): void {
    if (this.keyPairConfig[index]) {
      this.keyPairConfig[index][field] = value;
    }
  }

  // Authorization Tab Methods
  onAuthRequiredChange(checked: boolean): void {
    this.authRequired = checked;
    if (!this.authRequired && this.activeTab === 'authorization') {
      this.activeTab = 'parameters';
    }
  }

  onAuthorizationTypeChange(type: string): void {
    this.authorizationTypeName = type;
  }

  onAuthFieldChange(keyName: string, value: string): void {
    const authType = this.authorizationTypeName;
    const field = this.authorizationTypeData[authType]?.find(f => f.keyName === keyName);
    if (field) {
      field.value = value;
    }
  }

  // Response Tab Methods
  onSaveApiResponseChange(checked: boolean): void {
    this.isSaveApi = checked;
  }

  addMoreOption(index: number, field_id: string): void {
    const values = Object.values(this.selectedOptions);
    if (values.indexOf(index) < 0) {
      this.selectedOptions[field_id] = index;
      const actionConfig = this.configs[0];
      if (actionConfig && actionConfig.options.length !== this.configs.length) {
        const newConfig: ActionConfig = {
          key: null,
          value: null,
          index: this.configs.length + 1,
          options: actionConfig.options
        };
        this.configs.push(newConfig);
      }
    } else {
      // Update existing selection
      const mapping = { ...this.selectedOptions };
      Object.keys(mapping).forEach(key => {
        if (mapping[key] === index) {
          delete this.selectedOptions[key];
        }
      });
      this.selectedOptions[field_id] = index;
    }
  }

  removeItem(field_id: string): void {
    delete this.selectedOptions[field_id];
    this.configs = _.reject(this.configs, { key: field_id });
    if (!this.configs.length) {
      const defaultConfig: ActionConfig = {
        key: null,
        value: null,
        index: 1,
        options: []
      };
      this.configs = [defaultConfig];
    }
  }

  // Business Hours
  changeIsBusinessHour(isBusinessHour: boolean): void {
    this.performActionOnBusinessHour = isBusinessHour;
  }

  // Action Management
  addAction(): void {
    if (!this.selectedActionId) {
      this.showError('Please select an action');
      return;
    }

    // Prepare data before validation
    if (this.isAPIRequest) {
      this.prepareParamsData();
      this.prepareDropdownData();
      this.authorisationActionData();
    }

    const validationResult = this.validateAction();
    if (!validationResult.isValid) {
      this.showError(validationResult.message);
      return;
    }

    const action = this.prepareActionData();

    if (!this.workflow) {
      this.workflow = { actions: [] };
    }
    if (!this.workflow.actions) {
      this.workflow.actions = [];
    }

    if (this.actionIndexToUpdate !== null && this.actionIndexToUpdate !== undefined) {
      action.uuid = this.selectedActionUuid || action.uuid || '';
      this.workflow.actions[this.actionIndexToUpdate] = action;
      this.actionUpdated.emit({ index: this.actionIndexToUpdate, action });
    } else {
      this.workflow.actions.push(action);
      this.actionAdded.emit(action);
    }

    this.populateAvailableActions();
    this.resetForm();
  }

  updateAction(actionId: string, index: number): void {
    if (!actionId || index === null || index === undefined || !this.workflow?.actions?.[index]) {
      return;
    }

    this.actionIndexToUpdate = index;
    this.selectedActionUuid = this.workflow.actions[index]?.uuid || '';
    this.selectedActionId = actionId;

    if (_.isEmpty(this.actionsHash) || !this.actionsHash[actionId]) {
      this.pendingEdit = { actionId, index };
      if (_.isEmpty(this.actionsHash)) {
        this.fetchWorkflowActions();
      }
      return;
    }

    this.getFormBasedOnAction(actionId, index);
  }

  deleteAction(index: number): void {
    if (this.workflow?.actions && index >= 0 && index < this.workflow.actions.length) {
      this.workflow.actions.splice(index, 1);
      this.populateAvailableActions();
      this.actionDeleted.emit(index);
      // If deleting the action currently being edited, reset the form
      if (this.actionIndexToUpdate === index) {
        this.resetForm();
      } else if (this.actionIndexToUpdate !== null && this.actionIndexToUpdate > index) {
        this.actionIndexToUpdate = this.actionIndexToUpdate - 1;
      }
    }
  }

  validateAction(): { isValid: boolean; message: string } {
    if (this.isAPIRequest) {
      return this.validateAPIRequest();
    } else {
      return this.validateNonAPIRequest();
    }
  }

  validateAPIRequest(): { isValid: boolean; message: string } {
    let errorMessage = '';

    if (!this.requestOptions.requestUrl || this.requestOptions.requestUrl.trim() === '') {
      errorMessage += 'URL is mandatory.<br>';
    }
    if (!this.requestOptions.requestType) {
      errorMessage += 'Method is mandatory.<br>';
    }

    // Validate authorization fields
    if (this.authRequired) {
      const authType = this.authorizationTypeName;
      const authFields = this.authorizationTypeData[authType] || [];
      let hasEmptyField = false;

      authFields.forEach(field => {
        if (field.keyName !== 'bearer_token_body' && (!field.value || field.value.trim() === '')) {
          hasEmptyField = true;
        }
      });

      if (hasEmptyField) {
        errorMessage += 'Please enter required parameter for authorisation.<br>';
        this.isAuthData = true;
      }

      // Validate bearer token expression format
      if (authType === 'bearerToken') {
        const bearerTokenField = authFields.find(f => f.keyName === 'bearer_token');
        if (bearerTokenField && bearerTokenField.value) {
          const tokenValue = bearerTokenField.value;
          const hasExpressionStart = /^{{/.test(tokenValue);
          const hasExpressionEnd = /}}$/.test(tokenValue);

          if (!hasExpressionStart || !hasExpressionEnd) {
            errorMessage += 'Bearer Token should be in expression format (e.g., {{apiResponse.variableName}}).<br>';
            this.authBearerToknFlag = true;
          } else {
            // Validate JSON in authorisation if expression is used
            try {
              const authJson = this.parseJsonData(this.requestOptions.authorisation.json);
              if (_.isEmpty(authJson)) {
                errorMessage += 'Please enter parameters in JSON for bearer token expression.<br>';
                this.authBearerToknFlag = true;
              } else {
                this.authBearerToknFlag = false;
              }
            } catch (e) {
              errorMessage += 'Invalid JSON syntax in authorization parameters.<br>';
              this.authBearerToknFlag = true;
            }
          }
        }
      }
    }

    // Validate JSON parameters
    if (!this.parameterKeyPair) {
      try {
        const paramsData = this.parseJsonData(this.requestOptions.parameters.json);
        this.paramsJsonFlag = false;
      } catch (e) {
        errorMessage += 'Invalid JSON in parameters. Please correct the syntax.<br>';
        this.paramsJsonFlag = true;
      }
    }

    // Validate response config
    if (this.isSaveApi) {
      const responseConfig = this.prepareResponseConfig();
      if (_.isEmpty(responseConfig)) {
        errorMessage += 'Responses cannot be empty. Please select dropdown for responses.<br>';
        this.apiResponseFlag = true;
      } else {
        this.apiResponseFlag = false;
      }
    }

    return {
      isValid: errorMessage === '',
      message: errorMessage || 'Validation successful'
    };
  }

  validateNonAPIRequest(): { isValid: boolean; message: string } {
    let errorMessage = '';

    for (const key in this.optionsBasedOnActions) {
      const option = this.optionsBasedOnActions[key];
      if (key === 'uuid') {
        continue;
      }
      // Skip fields hidden by show-if
      if (!this.shouldShowField(option)) {
        continue;
      }
      const isMandatory = option.is_mandatory === undefined || option.is_mandatory === true;
      if (option.required && isMandatory) {
        const value = option.valueToSet;
        const isEmpty = value === undefined || value === null || value === '' ||
          (typeof value === 'object' && _.isEmpty(value?.data || value));
        if (isEmpty) {
          errorMessage += `${option.name || key} is mandatory.<br>`;
        }
      }
    }

    return {
      isValid: errorMessage === '',
      message: errorMessage || 'Validation successful'
    };
  }

  prepareActionData(): any {
    if (this.isAPIRequest) {
      return this.prepareAPIActionData();
    } else {
      return this.prepareNonAPIActionData();
    }
  }

  prepareAPIActionData(): ApiAction {
    const options: ApiOptions = {
      method: this.requestOptions?.requestType || '',
      url: this.requestOptions?.requestUrl || '',
      authentication_required: this.authRequired,
      data: this.parameterKeyPair ? this.prepareKeyValueData() : this.parseJsonData(this.requestOptions.parameters.json),
      should_save_api_response: this.isSaveApi,
      api_response_config: this.prepareResponseConfig(),
      authorization: {
        auth_type: (this.authRequired) ? this.authorizationTypeNameMap[this.authorizationTypeName] || '' : '',
        body: (this.authRequired) ? this.prepareAuthorizationBody() : {}
      },
      auth_params: this.parseJsonData(this.requestOptions.authorisation.json),
      parameter_json: !this.parameterKeyPair,
      log_text: this.requestOptions?.log_text || ''
    };

    const action: any = {
      action_id: 'APIRequest',
      options,
      retry: this.retry,
      uuid: this.selectedActionUuid || this.optionsBasedOnActions?.['uuid']?.valueToSet || '',
      should_execute_in_business_hours: !!this.performActionOnBusinessHour
    };

    if (!action.should_execute_in_business_hours) {
      delete action.timings;
    }

    return action;
  }

  prepareNonAPIActionData(): any {
    const action: any = {
      action_id: this.selectedActionId,
      name: this.actionsHash[this.selectedActionId]?.name || '',
      options: {},
      retry: this.retry,
      uuid: this.selectedActionUuid || this.optionsBasedOnActions?.['uuid']?.valueToSet || '',
      should_execute_in_business_hours: !!this.performActionOnBusinessHour
    };

    if (!action.should_execute_in_business_hours) {
      delete action.timings;
    }

    // Matching old prepareActionSchema AutoUpdate handling
    if (this.selectedActionId === 'AutoUpdate') {
      this.updateOptionsForAutoUpdate(this.optionsBasedOnActions, action.options);
    }

    for (const key in this.optionsBasedOnActions) {
      const option = this.optionsBasedOnActions[key];
      if (key === 'uuid') {
        continue;
      }

      const isMandatory = option.is_mandatory === undefined || option.is_mandatory === true;
      if (option.valueToSet !== undefined && option.valueToSet !== null && option.valueToSet !== '' && isMandatory) {
        if (option.type === 'object' && option.valueToSet?.data) {
          action.options[key] = option.valueToSet.data;
        } else {
          action.options[key] = option.valueToSet;
        }
      }
    }

    // Handle special action types
    if (this.selectedActionId === 'SMSNotify') {
      if (this.principal_entity_id) {
        action.options.principal_entity_id = this.principal_entity_id;
      }
      if (this.content_template_id) {
        action.options.content_template_id = this.content_template_id;
      }
    }
    if (this.selectedActionId === 'WhatsAppNotify') {
      if (this.template_language) {
        action.options.template_language = this.template_language;
      }
      if (this.template_text) {
        action.options.template_text = this.template_text;
      }
    }

    return action;
  }

  /**
   * Matching old updateOptionsForAutoUpdate — maps type to field_id / field_type / field_value
   */
  private updateOptionsForAutoUpdate(options: { [key: string]: ActionOption }, fieldToUpdate: any): void {
    const selectedType = this.utils.getProp(options, 'type.valueToSet');
    switch (selectedType) {
      case '1':
        fieldToUpdate.field_id = 'status_id';
        fieldToUpdate.field_type = 1;
        break;
      case '2':
        fieldToUpdate.field_id = 'category_id_list';
        fieldToUpdate.field_type = 1;
        break;
      case '3':
        fieldToUpdate.field_id = 'tag_id_list';
        fieldToUpdate.field_type = 1;
        break;
      case '4':
        fieldToUpdate.field_id = 'notes';
        fieldToUpdate.field_type = 3;
        break;
      case '5':
        fieldToUpdate.field_id = 'lw_custom_field';
        fieldToUpdate.field_type = 2;
        break;
    }
    fieldToUpdate.field_value = this.utils.getProp(options, `field_id_${selectedType}.valueToSet`);
  }

  prepareKeyValueData(): any {
    const data: any = {};
    this.keyPairConfig.forEach(config => {
      if (config.key && config.value) {
        data[config.key] = config.value;
      }
    });
    return data;
  }

  prepareAuthorizationBody(): any {
    const body: any = {};
    const authType = this.authorizationTypeName;
    const authFields = this.authorizationTypeData[authType] || [];

    authFields.forEach(field => {
      if (field.keyName !== 'bearer_token_body') {
        body[field.keyName] = field.value;
      }
    });

    return body;
  }

  prepareResponseConfig(): any {
    const config: any = {};
    this.configs.forEach(configItem => {
      if (configItem.key && configItem.value) {
        config[configItem.key] = configItem.value;
      }
    });
    return config;
  }

  parseJsonData(jsonString: string): any {
    try {
      return JSON.parse(jsonString || '{}');
    } catch (e) {
      return {};
    }
  }

  getFieldDetails(): void {
    // Fetch field definitions from brand details (custom_parameter_type == 4)
    const brandDetails = this.helpers.getBrandDetails();
    const parameters = _.filter(_.get(brandDetails, 'field_definitions', []), (field: any) => {
      return field.custom_parameter_type === 4;
    });

    const defaultConfig: ActionConfig = {
      key: null,
      value: null,
      index: 1,
      options: parameters || []
    };

    if (this.actionIndexToUpdate === null) {
      this.configs = [defaultConfig];
    } else {
      // Update existing configs with options
      this.configs.forEach(config => {
        config.options = parameters || [];
      });
    }
  }

  prepareUpdateData(updateOptions: any): void {
    this.requestOptions.log_text = updateOptions.options?.log_text || '';
    this.requestOptions.parameters.json = JSON.stringify(updateOptions.options?.data || {}, null, 2);
    this.requestOptions.authorisation.json = JSON.stringify(updateOptions.options?.auth_params || {}, null, 2);
    this.requestOptions.requestType = updateOptions.options?.method || 'POST';
    this.requestOptions.requestUrl = updateOptions.options?.url || '';
    this.authRequired = updateOptions.options?.authentication_required || false;
    this.retry = updateOptions.retry || false;
    this.isSaveApi = updateOptions.options?.should_save_api_response || false;

    if (this.authRequired && updateOptions.options?.authorization) {
      this.updateAuthData(updateOptions.options.authorization.auth_type, updateOptions.options.authorization.body);
    }

    if (this.isSaveApi && updateOptions.options?.api_response_config) {
      this.updateResponseData(updateOptions.options.api_response_config);
    }

    if (updateOptions.should_execute_in_business_hours) {
      this.changeIsBusinessHour(true);
    } else {
      this.changeIsBusinessHour(false);
    }

    // Update key-value pairs
    if (updateOptions.options?.data) {
      this.updateKeyValueConfig(updateOptions.options.data, updateOptions.options.parameter_json);
    }
  }

  updateAuthData(authType: string, authData: any): void {
    const authTypeName = Object.keys(this.authorizationTypeNameMap).find(
      key => this.authorizationTypeNameMap[key] === authType
    );

    if (authTypeName) {
      this.authorizationTypeName = authTypeName;
      const authFields = this.authorizationTypeData[authTypeName] || [];
      authFields.forEach(field => {
        if (authData[field.keyName]) {
          field.value = authData[field.keyName];
        }
      });
    }
  }

  updateResponseData(apiResponseData: any): void {
    this.configs = [];
    this.selectedOptions = {};
    let index = 0;

    Object.keys(apiResponseData).forEach(key => {
      const config: ActionConfig = {
        key,
        value: apiResponseData[key],
        index: ++index,
        options: []
      };
      this.selectedOptions[key] = config.index;
      this.configs.push(config);
    });

    const defaultConfig: ActionConfig = {
      key: null,
      value: null,
      index: ++index,
      options: []
    };
    this.configs.push(defaultConfig);
  }

  updateKeyValueConfig(data: any, isJson: boolean): void {
    if (isJson) {
      this.parameterKeyPair = false;
      this.keyPairConfig = [{ key: null, value: null, index: 0 }];
    } else {
      this.parameterKeyPair = true;
      this.keyPairConfig = [];
      let index = 0;
      if (data && typeof data === 'object') {
        Object.keys(data).forEach(key => {
          if (data[key] !== undefined && data[key] !== null) {
            this.keyPairConfig.push({
              key,
              value: String(data[key]),
              index: index++
            });
          }
        });
      }
      // Always add one empty row at the end
      this.keyPairConfig.push({ key: null, value: null, index: index });
    }
  }

  resetForm(): void {
    this.selectedActionId = '';
    this.selectedActionUuid = '';
    this.actionIndexToUpdate = null;
    this.pendingEdit = null;
    this.isAPIRequest = false;
    this.optionsBasedOnActions = {};
    this.renderedOptionKeys = [];
    this.keyPairConfig = [{ key: null, value: null, index: 0 }];
    this.configs = [{ key: null, value: null, index: 1, options: [] }];
    this.requestOptions = {
      requestType: 'POST',
      requestUrl: '',
      urlMarginLeft: '0px',
      parameters: { json: '{}' },
      authorisation: { json: '{}' },
      log_text: ''
    };
    this.authRequired = false;
    this.retry = false;
    this.performActionOnBusinessHour = false;
    this.isSaveApi = false;
    this.parameterKeyPair = true;
    this.activeTab = 'parameters';
    this.selectedOptions = {};

    // Reset validation flags
    this.paramsJsonFlag = false;
    this.authBearerToknFlag = false;
    this.isAuthData = false;
    this.authErrorMsg = '';
    this.apiResponseFlag = false;

    // Reset authorization fields
    Object.keys(this.authorizationTypeData).forEach(key => {
      this.authorizationTypeData[key].forEach(field => {
        field.value = '';
      });
    });

    // Reset special action fields
    this.principal_entity_id = '';
    this.content_template_id = '';
    this.template_language = '';
    this.template_text = '';
    this.emailNotificationActionTemplateConfig = '';
    this.whatsappNotificationActionTemplateConfig = '';
    this.smsNotificationActionTemplateConfig = '';
    this.openDropdownId = null;

    this.authorizationTypeName = 'oauth';
    this.formReset.emit();
    this.cdr.detectChanges();
  }

  showError(message: string): void {
    if (Helpers && Helpers.smallAlertBox) {
      Helpers.smallAlertBox('Error', message, 'error', 3000);
    } else if (this.helpers?.helpers?.smallAlertBox) {
      this.helpers.helpers.smallAlertBox('Error', message, 'error', 3000);
    }
  }

  // Prepare authorization data
  authorisationActionData(): void {
    this.isAuthData = false;
    this.authErrorMsg = '';

    if (this.authRequired) {
      const authType = this.authorizationTypeName;
      const authFields = this.authorizationTypeData[authType] || [];

      authFields.forEach(field => {
        if (field.keyName !== 'bearer_token_body' && (!field.value || field.value.trim() === '')) {
          this.isAuthData = true;
          this.authErrorMsg = 'Please enter required parameter for authorisation.';
        }
      });
    }
  }

  // Prepare parameters data
  prepareParamsData(): void {
    if (this.isAPIRequest) {
      if (this.parameterKeyPair) {
        // Already handled in prepareKeyValueData
      } else {
        // Validate JSON
        try {
          this.parseJsonData(this.requestOptions.parameters.json);
          this.paramsJsonFlag = false;
        } catch (e) {
          this.paramsJsonFlag = true;
        }
      }
    }
  }

  // Prepare dropdown data for response config
  prepareDropdownData(): void {
    if (this.isSaveApi) {
      const responseConfig = this.prepareResponseConfig();
      if (_.isEmpty(responseConfig)) {
        this.apiResponseFlag = true;
      } else {
        this.apiResponseFlag = false;
      }
    }
  }
}
